import { createContext, useContext, useState } from 'react';
import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  SvgIcon,
  SxProps,
} from '@mui/material';
import { CloseIconSx, PaperSx } from './DialogProps';
import CloseIcon from '../../assets/icons/CloseIcon';
import { ComponentTestIds } from '../../utils/dataTestIds';
import DiscardConfirmationDialog from './DiscardConfirmationDialog';

export const DialogRequestCloseContext = createContext<() => void>(() => {});

export const useDialogRequestClose = () =>
  useContext(DialogRequestCloseContext);

export type Props = MuiDialogProps & {
  paperSx?: SxProps;
  disableBackdropClick?: boolean;
  isDirty?: boolean;
  onDiscard?: () => void;
  onClose: (
    e: {},
    reason: 'backdropClick' | 'escapeKeyDown' | 'closeIconClick'
  ) => void;
};

function Dialog({
  paperSx,
  children,
  disableBackdropClick = true,
  isDirty = false,
  onDiscard,
  ...rest
}: Props) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const closeDialog = () => rest.onClose({}, 'closeIconClick');

  const requestClose = () => {
    if (onDiscard && isDirty) {
      setIsConfirmOpen(true);
      return;
    }
    if (onDiscard) {
      onDiscard();
    }
    closeDialog();
  };

  const handleClose: Props['onClose'] = (_e, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') return;
    requestClose();
  };

  const handleConfirmDiscard = () => {
    setIsConfirmOpen(false);
    onDiscard!();
    closeDialog();
  };

  const handleCancelDiscard = () => setIsConfirmOpen(false);

  return (
    <DialogRequestCloseContext.Provider value={requestClose}>
      <MuiDialog
        {...rest}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: { ...PaperSx, ...paperSx } as SxProps,
          },
        }}
      >
        <SvgIcon
          sx={CloseIconSx}
          onClick={requestClose}
          data-testid={ComponentTestIds.Dialog.CloseIcon}
        >
          <CloseIcon />
        </SvgIcon>
        {children}
      </MuiDialog>
      {onDiscard && (
        <DiscardConfirmationDialog
          open={isConfirmOpen}
          onConfirm={handleConfirmDiscard}
          onCancel={handleCancelDiscard}
        />
      )}
    </DialogRequestCloseContext.Provider>
  );
}

export default Dialog;
